import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import ForDevelopersSection from '../components/ForDevelopersSection';
import CTASection from '../components/CTASection';
import { 
  ArrowRight,
  Buildings,
  ShieldCheck,
  Lightbulb,
  Lightning,
  Target,
  Heart,
  Globe,
  LinkedinLogo,
  CheckCircle,
  Percent,
  Scales,
  Plus,
  EnvelopeSimple,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const AboutPage = () => {
  const navigate = useNavigate();
  const { openAuthModal } = useAuth();

  // Core values - updated copy
  const values = [
    {
      icon: ShieldCheck,
      title: 'Transparency First',
      description: 'Complete visibility into where your money goes, how it performs, and what you can expect.',
    },
    {
      icon: Lightbulb,
      title: 'Simplicity',
      description: 'We believe the investment should be simpler than the property. Complexity is our problem, not yours.',
    },
    {
      icon: Target,
      title: 'Quality Over Quantity',
      description: 'Every opportunity undergoes rigorous due diligence. We only list projects we believe in.',
    },
    {
      icon: Heart,
      title: 'Investor First',
      description: 'Your success is our success. Your interests always come first.',
    },
  ];

  // Stats
  const stats = [
    { value: '€2.0M+', label: 'Raised' },
    { value: '€250k+', label: 'Paid in returns' },
    { value: '€100', label: 'Minimum investment' },
    { value: '5', label: 'Properties funded' },
  ];

  // Why now
  const whyNow = [
    {
      icon: Buildings,
      title: 'Portugal is short of homes',
      description:
        'Demand keeps outrunning supply in Lisbon and Porto, and the gap is being filled project by project. Capital is the constraint — which is exactly what a marketplace can solve.',
    },
    {
      icon: Percent,
      title: 'Rates have turned again',
      description:
        'Deposits no longer pay what they did in 2023. Investors are hunting real yield again, and are willing to trade a little liquidity to get it.',
    },
    {
      icon: Scales,
      title: 'The rules finally exist',
      description:
        'MiCA and the EU pilot regime gave tokenised, asset-backed investments a legal frame to operate inside. What was a grey area in 2021 is now a licensable business.',
    },
    {
      icon: Lightning,
      title: 'The €100 ticket is possible',
      description:
        'Digital onboarding, instant payments and on-chain registers made small tickets economical to service. The minimum used to be set by paperwork, not by the asset.',
    },
  ];

  // Team members
  const team = [
    {
      name: 'André Valera',
      initials: 'AV',
      photo: '/team/andre-valera.jpg',
      focus: '58% 20%',
      role: 'Co-Founder',
      bio: 'A decade investing in and renovating Portuguese property — the deals that became the Flippex model. Two decades leading ventures in aviation and travel tech, most recently as CEO of Aeroplay. Studied blockchain and tokenisation at Católica Lisbon.',
      linkedin: 'https://www.linkedin.com/in/andrevalera/',
    },
    {
      name: 'Daniel Costa',
      initials: 'DC',
      photo: '/team/daniel-costa.jpg',
      focus: '45% 18%',
      role: 'Co-Founder',
      bio: 'Finance and operations across borders — VP Finance & Administration at Conetic, CFO at Mobipium, M&A at Deloitte. Economics at ISCTE, MBA at INDEG-ISCTE.',
      linkedin: 'https://www.linkedin.com/in/ddcosta/',
    },
  ];

  const joinMailto =
    'mailto:andre.valera@flippex.com' +
    '?subject=' + encodeURIComponent('The next one could be me') +
    '&body=' + encodeURIComponent(
      "Hi André,\n\nI saw the open seat on the Flippex team page.\n\nHere's what I do, and where I think I'd help:\n\n"
    );

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="about-page">
      <Header />

      {/* Hero Section - Updated copy */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-slate-900" />
        <div className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-medium text-primary tracking-[0.2em] uppercase mb-6">
              About Flippex
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-medium text-white mb-6 tracking-tight leading-[1.1]">
              There had to be a <span className="text-primary">better way</span> into real estate.
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto">
              So we built it.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section - Updated copy */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm font-medium text-primary tracking-[0.2em] uppercase mb-4">
                Our Mission
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium mb-8 tracking-tight leading-[1.1]">
                Real estate without the real-estate-sized cheque.
              </h2>
              <p className="text-xl text-foreground-muted mb-6 leading-relaxed font-light">
                Traditional real estate investing has always assumed you need enough capital to purchase 
                an entire property, obtain financing, take on debt and potentially become a landlord.
              </p>
              <p className="text-xl text-foreground-muted mb-6 leading-relaxed font-light">
                We challenged that assumption.
              </p>
              <p className="text-xl text-foreground leading-relaxed">
                Flippex is where investors access curated real-estate projects starting from €100. 
                No mortgage. No tenants. No property management.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Team collaboration"
                  className="w-full h-[450px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-primary text-white rounded-2xl p-6 shadow-xl max-w-xs">
                <Globe size={28} weight="duotone" className="mb-3" />
                <p className="font-medium">Based in Lisbon, serving investors across Europe.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[rgb(var(--background))]">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-2">
                  {stat.value}
                </p>
                <p className="text-foreground-muted">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section - Updated copy */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-primary tracking-[0.2em] uppercase mb-4">
              Our Values
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium mb-4 tracking-tight">
              What we stand for.
            </h2>
            <p className="text-lg text-foreground-muted max-w-2xl mx-auto">
              These principles guide everything we do.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="p-8 rounded-3xl bg-[rgb(var(--background))] border border-black/[0.04] hover:border-primary/20 hover:shadow-lg transition-all duration-300"
                >
                  <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                    <Icon className="text-primary" size={28} weight="duotone" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                  <p className="text-foreground-muted leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-24 md:py-32 bg-slate-900 overflow-hidden" data-testid="why-now-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
              <p className="text-sm font-medium text-primary tracking-[0.2em] uppercase mb-4">
                Why Now
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium text-white mb-8 tracking-tight leading-[1.1]">
                This was not possible five years ago.
              </h2>
              <p className="text-lg text-white/60 leading-relaxed font-light">
                Four things had to line up at once: a market that needs capital, investors who want
                yield again, regulation that permits it, and technology cheap enough to make €100
                worth processing. They finally have.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="divide-y divide-white/10 border-y border-white/10">
                {whyNow.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className="group flex gap-6 py-8 transition-colors duration-300 hover:bg-white/[0.03]"
                      data-testid={`why-now-item-${index}`}
                    >
                      <div className="shrink-0">
                        <div className="h-12 w-12 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center transition-colors duration-300 group-hover:bg-primary/20 group-hover:border-primary/30">
                          <Icon className="text-primary" size={24} weight="duotone" />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="text-xs font-mono text-white/30">
                            0{index + 1}
                          </span>
                          <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                        </div>
                        <p className="text-white/60 leading-relaxed font-light">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 bg-[rgb(var(--background))]">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-primary tracking-[0.2em] uppercase mb-4">
              Our Team
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium mb-4 tracking-tight">
              The people behind Flippex.
            </h2>
            <p className="text-lg text-foreground-muted max-w-2xl mx-auto">
              Small on purpose. Operators who have built and financed businesses before, now doing it
              for real estate.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl overflow-hidden border border-black/[0.04] hover:shadow-lg transition-all duration-300"
                data-testid={`team-member-${index}`}
              >
                <div className="aspect-square bg-slate-900 overflow-hidden">
                  <img
                    src={member.photo}
                    alt={member.name}
                    loading="lazy"
                    style={{ objectPosition: member.focus }}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                    data-testid={`team-member-photo-${index}`}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-1">{member.name}</h3>
                  <p className="text-sm text-primary font-medium mb-3">{member.role}</p>
                  <p className="text-sm text-foreground-muted mb-4">{member.bio}</p>
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-primary transition-colors"
                    data-testid={`team-member-linkedin-${index}`}
                  >
                    <LinkedinLogo size={18} weight="fill" />
                    LinkedIn
                  </a>
                </div>
              </div>
            ))}

            <a
              href={joinMailto}
              className="group block bg-white rounded-3xl overflow-hidden border border-dashed border-primary/30 hover:border-primary/60 hover:shadow-lg transition-all duration-300"
              data-testid="team-join-cta"
            >
              <div className="aspect-square bg-primary/[0.06] flex items-center justify-center transition-colors duration-300 group-hover:bg-primary/10">
                <div className="h-16 w-16 rounded-2xl bg-white border border-primary/20 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                  <Plus className="text-primary" size={28} weight="bold" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-1">The next one could be you.</h3>
                <p className="text-sm text-primary font-medium mb-3">Open seat</p>
                <p className="text-sm text-foreground-muted mb-4">
                  We hire slowly and give away a lot of responsibility. If you would back this
                  yourself, tell us what you would own.
                </p>
                <span className="inline-flex items-center gap-2 text-sm text-primary font-medium">
                  <EnvelopeSimple size={18} weight="fill" />
                  Email André
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* For Developers Section */}
      <ForDevelopersSection />

      {/* CTA Section */}
      <CTASection />

      <Footer />
    </div>
  );
};

export default AboutPage;
